import React, { useEffect, useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Download, Loader2, X } from "lucide-react";
import { addDoc, collection, getDocs, query, where, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { normalizeReraFromRaw, normalizeHaryanaFromRaw } from "../utils/normalizeRera";
import featureGatingService from "../../services/featureGatingService";
import { toast } from "react-toastify";

function ReraCaseDetails() {
  const API_BASE = "https://jreraa.valscotech.com";
  const HARYANA_API_BASE = "https://rera-haryana-596718606544.asia-south1.run.app";
  const MAX_VISIBLE_ROWS = 5;


  const { filingNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isHaryana = location.state?.stateCode === "HARYANA";
  const haryanaInternalId = location.state?.internalId || "";

  const getSessionId = () => {
    if (isHaryana) {
      return sessionStorage.getItem("haryanaReraSessionId") ||
        localStorage.getItem("haryanaReraSessionId");
    }
    return sessionStorage.getItem("upReraSessionId") ||
      localStorage.getItem("upReraSessionId");
  };


  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hearingHtml, setHearingHtml] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Modal states for View All
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [viewAllModalData, setViewAllModalData] = useState({ title: "", type: "", data: null });

  // Get case title from location state (passed from ReraCaseList)
  const caseTitle = location.state?.caseTitle || "";


  const handleViewHearing = async ({ filingNo, listingDate }) => {
  try {

const res = await fetch(
  `${API_BASE}/api/rera/up/getHearingDetails`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: getSessionId(),
          filingNo,
          listingDate,
        }),
      }
    );

    const newSessionId = res.headers.get("X-UpRera-SessionId");
 if (newSessionId) {
   sessionStorage.setItem("upReraSessionId", newSessionId);
   localStorage.setItem("upReraSessionId", newSessionId);
 }

    const html = await res.text(); // ✅ CORRECT

    if (!res.ok) {
      console.error("Hearing fetch failed:", html);
      alert("Unable to load hearing details");
      return;
    }

    const w = window.open("", "_blank");
    w.document.open();
    w.document.write(html);
    w.document.close();
  } catch (e) {
    console.error(e);
    alert("Error loading hearing details");
  }
};

  // Export case function
  const handleExportCase = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("User not authenticated");
        return;
      }

      if (!data) {
        toast.error("No case data to export");
        return;
      }

      setExporting(true);

      // Determine the filing/case identifier for duplicate check
      const duplicateCheckId = isHaryana
        ? (data.complaintNo || filingNo)
        : filingNo;

      // Check for duplicates
      const pendingQ = query(
        collection(db, "pending"),
        where("filingNo", "==", duplicateCheckId),
        where("owner", "==", user.uid)
      );
      const disposedQ = query(
        collection(db, "disposed"),
        where("filingNo", "==", duplicateCheckId),
        where("owner", "==", user.uid)
      );

      const [pendingSnap, disposedSnap] = await Promise.all([
        getDocs(pendingQ),
        getDocs(disposedQ),
      ]);

      if (!pendingSnap.empty || !disposedSnap.empty) {
        toast("This case is already saved in your cases.", { icon: "⚠️" });
        setExporting(false);
        return;
      }

      let normalized;
      let caseDataToExport;

      if (isHaryana) {
        normalized = normalizeHaryanaFromRaw(data);
        const actualCaseNoH = normalized.caseNumber || filingNo;

        caseDataToExport = {
          ...normalized,
          filingNo: actualCaseNoH,
          caseNumber: actualCaseNoH,
          caseNo: actualCaseNoH,
          petitionerName: normalized.petitionerName,
          respondentName: normalized.respondentName,
          rawReraData: data,
          owner: user.uid,
          team: [user.uid],
          createdAt: new Date(),
        };
      } else {
        // Extract Case No from Case Detail section (UP/Delhi - sections is an array)
        const caseDetailSection = data.sections?.find(
          s => s.title?.toLowerCase().includes("case detail") && s.type === "key-value"
        );
        const caseNoFromSection = caseDetailSection?.data?.["Case No"]
          || caseDetailSection?.data?.["Case No."]
          || caseDetailSection?.data?.["CaseNo"]
          || Object.entries(caseDetailSection?.data || {}).find(([k]) => k.toLowerCase().replace(/[^a-z]/g, '') === 'caseno')?.[1];
        const actualCaseNo = caseNoFromSection || filingNo;

        // Extract petitioner and respondent
        let petitioner = "N/A";
        let respondent = "N/A";

        if (caseTitle) {
          const titleParts = caseTitle.split(/\s+VS\s+/i);
          petitioner = titleParts[0]?.trim() || "N/A";
          respondent = titleParts[1]?.trim() || "N/A";
        }

        if (petitioner === "N/A" || respondent === "N/A") {
          const partySection = data.sections?.find(
            s => s.title?.toLowerCase().includes("party detail") && s.type === "split-table"
          );
          if (partySection?.data) {
            const petitionerTable = partySection.data[0];
            const respondentTable = partySection.data[1];
            if (petitionerTable?.rows?.[0]) {
              const petKey = Object.keys(petitionerTable.rows[0]).find(k => k !== "Sr. No.");
              if (petKey) petitioner = petitionerTable.rows[0][petKey] || petitioner;
            }
            if (respondentTable?.rows?.[0]) {
              const resKey = Object.keys(respondentTable.rows[0]).find(k => k !== "Sr. No.");
              if (resKey) respondent = respondentTable.rows[0][resKey] || respondent;
            }
          }
        }

        normalized = normalizeReraFromRaw(data.sections || []);
        caseDataToExport = {
          ...normalized,
          filingNo,
          caseNumber: actualCaseNo,
          caseNo: actualCaseNo,
          petitionerName: petitioner,
          respondentName: respondent,
          rawReraData: {
            sections: data.sections || [],
            orders: data.orders || [],
          },
          owner: user.uid,
          team: [user.uid],
          createdAt: new Date(),
        };
      }

      const targetCollection = caseDataToExport.status || "pending";

      // Check case limit before proceeding
      const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
        user.uid,
        1,
        targetCollection === "disposed" ? "disposed" : "pending"
      );

      if (!limitResult.canProceed) {
        toast.error(`Case limit reached! Your ${limitResult.planName} plan allows ${limitResult.maxLimit} ${targetCollection} cases. Please upgrade to add more.`);
        setExporting(false);
        return;
      }

      // Get subscription metadata for pending cases
      let subscriptionMetadata = null;
      if (targetCollection === "pending") {
        const pendingSnap0 = await getDocs(query(collection(db, "pending"), where("owner", "==", user.uid)));
        subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
          user.uid,
          pendingSnap0.size
        );
      }

      // Add payload, metadata and timestamp
      caseDataToExport.payload = {
        exportedAt: new Date().toISOString(),
        caseexportedfrom: "RERA",
      };
      if (subscriptionMetadata) {
        caseDataToExport.subscriptionMetadata = subscriptionMetadata;
      }
      caseDataToExport.timestamp = serverTimestamp();

      const docRef = await addDoc(
        collection(db, targetCollection),
        caseDataToExport
      );

      const caseId = docRef.id;

      await updateDoc(docRef, {
        caseFilesLink: `/portal/case-files/${caseId}?from=rera`,
      });

      // Create hearing reminder events (only if next hearing date is in the future)
      try {
        const caseNoForEvent = caseDataToExport.caseNo || caseDataToExport.caseNumber || "";
        const petName = caseDataToExport.petitionerName || "";
        const resName = caseDataToExport.respondentName || "";
        const nextHearingRaw = caseDataToExport.nextHearingDate;

        const parseHearingDate = (str) => {
          if (!str) return null;
          const iso = String(str).match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
          if (iso) return new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3]));
          const dmy = String(str).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
          if (dmy) return new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
          return null;
        };
        const formatYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        const eventDate = parseHearingDate(nextHearingRaw);
        if (eventDate && !isNaN(eventDate.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const hearingDay = new Date(eventDate);
          hearingDay.setHours(0, 0, 0, 0);

          if (hearingDay >= today) {
            const reminderDate = new Date(eventDate);
            reminderDate.setHours(8, 0, 0, 0);
            const eventTimeIST = new Date(eventDate);
            eventTimeIST.setHours(18, 0, 0, 0);
            const now = new Date();

            if (reminderDate > now) {
              const reminderEventData = {
                "Case No": caseNoForEvent,
                date: formatYMD(reminderDate),
                id: Math.floor(Math.random() * 90000) + 10000,
                time: "08:00 AM",
                time1: reminderDate.getTime(),
                title: `${petName} VS ${resName}`,
                description: `Hearing Reminder : Case No. - ${caseNoForEvent}`,
                isScheduled: true,
                status: targetCollection,
                eventType: "reminder",
              };
              const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), reminderEventData);
              await addDoc(collection(db, "eventReminders"), {
                eventId: reminderEventRef.id,
                reminderTime: reminderDate,
                eventTitle: `Hearing Reminder : Case No. - ${caseNoForEvent}`,
                caseNo: caseNoForEvent,
                caseId: caseId,
                collection: targetCollection,
                status: "pending",
                createdAt: new Date(),
                scheduledBy: user.uid,
                recipientId: user.uid,
              });
            }

            if (eventTimeIST > now) {
              const mainEventData = {
                "Case No": caseNoForEvent,
                date: formatYMD(eventDate),
                id: Math.floor(Math.random() * 90000) + 10000,
                time: "06:00 PM",
                time1: eventTimeIST.getTime(),
                title: `Update Next Hearing: ${petName} VS ${resName}`,
                description: `Case No. - ${caseNoForEvent}`,
                isScheduled: true,
                status: targetCollection,
                eventType: "updateReminder",
                isHidden: true,
              };
              const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), mainEventData);
              await addDoc(collection(db, "eventReminders"), {
                eventId: mainEventRef.id,
                reminderTime: eventTimeIST,
                eventTitle: `Update Next Hearing : ${caseNoForEvent}`,
                caseNo: caseNoForEvent,
                caseId: caseId,
                collection: targetCollection,
                status: "pending",
                createdAt: new Date(),
                scheduledBy: user.uid,
                recipientId: user.uid,
              });
            }
          } else {
            console.log("RERA: skipping event creation — next hearing date is in the past:", nextHearingRaw);
          }
        }
      } catch (notificationError) {
        console.error("RERA notification setup error:", notificationError);
      }

      // Save orders to files collection
      if (isHaryana) {
        const listings = data.sections?.complaint_listing_details || [];
        for (const hearing of listings) {
          if (!hearing?.Order_action?.url) continue;
          await addDoc(collection(db, "files"), {
            caseNo: caseDataToExport.caseNo,
            filingNo: caseDataToExport.filingNo,
            fileName: `Order_dated_on_${hearing["Date of Hearing"] || "Unknown Date"}`,
            file_url: hearing.Order_action.url,
            lawyer_uid: user.uid,
            uploadedAt: hearing["Order Uploaded On"] || new Date().toISOString(),
            source: "RERA",
            folder: "orders",
          });
        }
      } else if (Array.isArray(data.orders)) {
        for (const o of data.orders) {
          if (!o?.downloadLink) continue;
          await addDoc(collection(db, "files"), {
            caseNo: actualCaseNo,
            filingNo: filingNo,
            fileName: o.orderType || `RERA Order – ${o.orderDate || "Unknown Date"}`,
            file_url: o.downloadLink,
            lawyer_uid: user.uid,
            uploadedAt: o.uploadDate || new Date().toISOString(),
            source: "RERA",
          });
        }
      }

      toast.success("Case exported successfully!");

    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Error exporting case: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };



  useEffect(() => {
    const fetchDetails = async () => {
      try {
        if (isHaryana) {
          const sessionId = getSessionId();
          if (!sessionId) {
            throw new Error("Haryana session expired. Please search again.");
          }

          const res = await fetch(`${HARYANA_API_BASE}/api/rera/hr/getCaseDetails`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId, internalId: haryanaInternalId }),
          });

          const j = await res.json();
          if (!j.success) throw new Error(j.message || "No case details found");

          console.log("HARYANA CASE DATA:", j.data);
          setData(j.data);
        } else {
          let effectiveSessionId = getSessionId();

          if (!effectiveSessionId) {
            const capRes = await fetch(`${API_BASE}/api/rera/up/generateCaptcha`);
            const capData = await capRes.json();
            effectiveSessionId = capData.sessionId;
            sessionStorage.setItem("upReraSessionId", effectiveSessionId);
            localStorage.setItem("upReraSessionId", effectiveSessionId);
          }

          const res = await fetch(`${API_BASE}/api/rera/up/getCaseDetails`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId: effectiveSessionId, filingNo }),
          });

          const j = await res.json();
          if (!j.success) throw new Error(j.message || "No case details found");

          console.log("VIEW CASE DATA:", j.data);
          setData(j.data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [filingNo]);

  /* ---------------- STATES ---------------- */
if (loading) {
  return (
    <>
            <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading case details...</p>
        </div>
      </div>
    </>
  );
}


if (error || !data) {
  return (
    <>
            <div className="min-h-screen px-4 md:px-16 py-8 text-white">


        <div className="flex flex-col items-center justify-center mt-12">
          <p className="text-lg mb-4">
            {error || "Session expired. Please search again."}
          </p>
          <Link to="/portal/fetchfromrera" className="text-yellow-400 underline">
            Return to Search
          </Link>
        </div>
      </div>
    </>
  );
}
/* ✅ SAFE TO ACCESS DATA BELOW */
const sections = Array.isArray(data.sections) ? data.sections : [];
const orders = Array.isArray(data.orders) ? data.orders : [];

// Haryana-specific data
const hSections = (!Array.isArray(data.sections) && typeof data.sections === "object") ? data.sections : {};
const hDetailKey = Object.keys(hSections).find((k) => k.startsWith("complaint_detail"));
const hDetail = (hSections[hDetailKey] || [])[0] || {};
const hListings = hSections.complaint_listing_details || [];
const hDocuments = hSections.documents_submitted || [];


  return (
    <>
      
      
      

      <div className="relative min-h-screen flex flex-col px-4 md:px-16 py-8 text-white">
          <div className="mb-4"><FindCaseStepper current={4} /></div>

        <div className="relative z-10 flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center"
          >
            <ArrowLeft className="w-6 h-6 text-white cursor-pointer" />
            <h1 className="text-yellow-500 ml-4 font-bold text-2xl">
              RERA Case Details
            </h1>
          </button>

          <button
            onClick={handleExportCase}
            disabled={exporting || !data}
            className={`flex items-center gap-2 bg-white text-[#060125] rounded-md py-2 px-5 font-bold transition-all duration-200 ${
              exporting || !data
                ? "opacity-50 cursor-not-allowed bg-gray-300"
                : "hover:bg-gray-100 hover:shadow-lg active:scale-95"
            }`}
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export Case
              </>
            )}
          </button>
        </div>

        {/* ====================== HARYANA RENDERING ====================== */}
        {isHaryana && (
          <div className="space-y-6">
            {/* Complaint Detail */}
            {Object.keys(hDetail).length > 0 && (
              <div className="bg-white/10 rounded-2xl p-6">
                <h2 className="text-yellow-300 font-semibold mb-4">Complaint Detail</h2>
                <table className="w-full border border-white/20">
                  <tbody>
                    {Object.entries(hDetail).reduce((rows, [k, v], i, arr) => {
                      if (i % 2 === 0) {
                        rows.push(
                          <tr key={k} className="border-b border-white/10">
                            <td className="px-4 py-3 font-semibold w-1/4">{k}</td>
                            <td className="px-4 py-3 w-1/4">{v || "—"}</td>
                            <td className="px-4 py-3 font-semibold w-1/4">{arr[i + 1]?.[0] || ""}</td>
                            <td className="px-4 py-3 w-1/4">{arr[i + 1]?.[1] || ""}</td>
                          </tr>
                        );
                      }
                      return rows;
                    }, [])}
                  </tbody>
                </table>
              </div>
            )}

            {/* Hearing History */}
            {hListings.length > 0 && (
              <div className="bg-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-yellow-300 font-semibold">Hearing History</h2>
                  {hListings.length > MAX_VISIBLE_ROWS && (
                    <button
                      onClick={() => {
                        setViewAllModalData({ title: "Hearing History", type: "haryana-listings", data: hListings });
                        setShowViewAllModal(true);
                      }}
                      className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
                    >
                      View All ({hListings.length})
                    </button>
                  )}
                </div>
                <table className="w-full border border-white/20">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Proceedings</th>
                      <th className="px-4 py-2 text-left">Bench</th>
                      <th className="px-4 py-2 text-left">Order</th>
                      <th className="px-4 py-2 text-left">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hListings.slice(0, MAX_VISIBLE_ROWS).map((h, i) => (
                      <tr key={i} className="border-t border-white/10">
                        <td className="px-4 py-2 whitespace-nowrap">{h["Date of Hearing"] || "—"}</td>
                        <td className="px-4 py-2">{h.Status || "—"}</td>
                        <td className="px-4 py-2 text-sm max-w-md">{h["Proceedings of the day"] || "—"}</td>
                        <td className="px-4 py-2">{h.Bench || "—"}</td>
                        <td className="px-4 py-2">
                          {h.Order_action?.url ? (
                            <a
                              href={h.Order_action.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                            >
                              View
                            </a>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-2">
                          {h.Order_action?.url ? (
                            <a
                              href={`${HARYANA_API_BASE}/api/rera/hr/downloadOrder?sessionId=${getSessionId()}&url=${encodeURIComponent(h.Order_action.url)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-400 hover:text-green-300"
                            >
                              <Download className="w-5 h-5" />
                            </a>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Documents Submitted */}
            {hDocuments.length > 0 && (
              <div className="bg-white/10 rounded-2xl p-6">
                <h2 className="text-yellow-300 font-semibold mb-4">Documents Submitted</h2>
                <table className="w-full border border-white/20">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="px-4 py-2 text-left">Dak ID</th>
                      <th className="px-4 py-2 text-left">Receiving Date</th>
                      <th className="px-4 py-2 text-left">Submitted By</th>
                      <th className="px-4 py-2 text-left">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hDocuments.map((doc, i) => (
                      <tr key={i} className="border-t border-white/10">
                        <td className="px-4 py-2">{doc["Dak ID"] || "—"}</td>
                        <td className="px-4 py-2">{doc["Receiving Date"] || "—"}</td>
                        <td className="px-4 py-2">{doc["Submitted By"] || "—"}</td>
                        <td className="px-4 py-2">{doc.Remarks || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ====================== UP / DELHI RENDERING ====================== */}
        {/* ---------------- ORDER HISTORY ---------------- */}
{!isHaryana && orders.length > 0 && (
  <div className="bg-white/10 rounded-2xl p-6 mt-8">

    <div className="flex items-center justify-between mb-4">
      <h2 className="text-yellow-300 font-semibold">
        Order History
      </h2>
      {orders.length > MAX_VISIBLE_ROWS && (
        <button
          onClick={() => {
            setViewAllModalData({ title: "Order History", type: "orders", data: orders });
            setShowViewAllModal(true);
          }}
          className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
        >
          View All ({orders.length})
        </button>
      )}
    </div>

    <table className="w-full border border-white/20">
      <thead className="bg-white/10">
        <tr>
          <th className="px-4 py-2 text-left">Sr. No.</th>
          <th className="px-4 py-2 text-left">Upload Date</th>
          <th className="px-4 py-2 text-left">Order Date</th>
          <th className="px-4 py-2 text-left">Order Type</th>
          <th className="px-4 py-2 text-left">View</th>
        </tr>
      </thead>

      <tbody>
  {orders.slice(0, MAX_VISIBLE_ROWS).map((order, i) => {
    return (
      <tr key={i} className="border-t border-white/10">
        <td className="px-4 py-2">{order.srNo}</td>
        <td className="px-4 py-2">{order.uploadDate}</td>
        <td className="px-4 py-2">{order.orderDate}</td>
        <td className="px-4 py-2">{order.orderType}</td>
        <td className="px-4 py-2">
          {order.downloadLink ? (
            <a
              href={`${API_BASE}/api/rera/up/downloadOrder?sessionId=${getSessionId()}&url=${encodeURIComponent(order.downloadLink)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline cursor-pointer"
            >
              Download
            </a>
          ) : (
            "—"
          )}
        </td>
      </tr>
    );
  })}
</tbody>

    </table>
  </div>
)}


        {!isHaryana && <div className="space-y-6">
          <div className="space-y-6">
  {sections.map((section, idx) => (
    <div key={idx} className="bg-white/10 rounded-2xl p-6">
      <h2 className="text-yellow-300 font-semibold mb-4">
        {section.title}
      </h2>

      {/* ---------------- KEY VALUE (Case / Hearing details) ---------------- */}
      {section.type === "key-value" && (
        <table className="w-full border border-white/20">
          <tbody>
            {Object.entries(section.data).reduce((rows, [k, v], i, arr) => {
              if (i % 2 === 0) {
                rows.push(
                  <tr key={k} className="border-b border-white/10">
                    <td className="px-4 py-3 font-semibold w-1/4">{k}</td>
                    <td className="px-4 py-3 w-1/4">{v || "—"}</td>
                    <td className="px-4 py-3 font-semibold w-1/4">
                      {arr[i + 1]?.[0] || ""}
                    </td>
                    <td className="px-4 py-3 w-1/4">
                      {arr[i + 1]?.[1] || ""}
                    </td>
                  </tr>
                );
              }
              return rows;
            }, [])}
          </tbody>
        </table>
      )}

      {/* ---------------- SPLIT TABLE (Party / Legal Rep) ---------------- */}
      {section.type === "split-table" && (
        <div className="grid md:grid-cols-2 gap-6">
          {section.data.map((tbl, i) => (
            <table
              key={i}
              className="w-full border border-white/20"
            >
              <thead className="bg-white/10">
                <tr>
                  {tbl.headers.map((h, j) => (
                    <th key={j} className="px-4 py-2 text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tbl.rows.map((row, r) => (
                  <tr key={r} className="border-t border-white/10">
                    {tbl.headers.map(h => (
                      <td key={h} className="px-4 py-2">
  {row[h]?.type === "hearing" ? (
  <button
    onClick={() => handleViewHearing(row[h])}
    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
  >
    View
  </button>
) : (
  row[h] || "—"
)}


</td>

                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      )}

      {/* ---------------- GRID TABLE (Case history) ---------------- */}
      {section.type === "grid" && (
        <>
          {section.rows?.length > MAX_VISIBLE_ROWS && (
            <div className="flex justify-end mb-2">
              <button
                onClick={() => {
                  setViewAllModalData({ title: section.title, type: "grid", data: section });
                  setShowViewAllModal(true);
                }}
                className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
              >
                View All ({section.rows.length})
              </button>
            </div>
          )}
          <table className="w-full border border-white/20">
            <thead className="bg-white/10">
              <tr>
                {section.headers.map(h => (
                  <th key={h} className="px-4 py-2 text-left">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.slice(0, MAX_VISIBLE_ROWS).map((row, i) => (
                <tr key={i} className="border-t border-white/10">
                  {section.headers.map(h => (
                    <td key={h} className="px-4 py-2">
                      {row[h]?.type === "hearing" ? (
                        <button
                          onClick={() => handleViewHearing(row[h])}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                        >
                          View
                        </button>
                      ) : row[h]?.type === "order" ? (
                        <a
                          href={`${API_BASE}/api/rera/up/downloadOrder?sessionId=${getSessionId()}&url=${encodeURIComponent(row[h].downloadLink)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 underline"
                        >
                          Download
                        </a>
                      ) : (
                        row[h] || "—"
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  ))}
</div>


        </div>}
      </div>

      {/* View All Modal */}
      {showViewAllModal && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 pt-24 overflow-y-auto">
          <div className="bg-[#1a1a2e] rounded-2xl border border-white/20 max-w-5xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/20">
              <h2 className="text-yellow-300 font-semibold text-xl">
                {viewAllModalData.title}
              </h2>
              <button
                onClick={() => setShowViewAllModal(false)}
                className="text-slate-500 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {viewAllModalData.type === "orders" && (
                <table className="w-full border border-white/20">
                  <thead className="bg-white/10 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Sr. No.</th>
                      <th className="px-4 py-2 text-left">Upload Date</th>
                      <th className="px-4 py-2 text-left">Order Date</th>
                      <th className="px-4 py-2 text-left">Order Type</th>
                      <th className="px-4 py-2 text-left">View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewAllModalData.data?.map((order, i) => (
                      <tr key={i} className="border-t border-white/10">
                        <td className="px-4 py-2">{order.srNo}</td>
                        <td className="px-4 py-2">{order.uploadDate}</td>
                        <td className="px-4 py-2">{order.orderDate}</td>
                        <td className="px-4 py-2">{order.orderType}</td>
                        <td className="px-4 py-2">
                          {order.downloadLink ? (
                            <a
                              href={`${API_BASE}/api/rera/up/downloadOrder?sessionId=${getSessionId()}&url=${encodeURIComponent(order.downloadLink)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 underline cursor-pointer"
                            >
                              Download
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {viewAllModalData.type === "haryana-listings" && Array.isArray(viewAllModalData.data) && (
                <table className="w-full border border-white/20">
                  <thead className="bg-white/10 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Proceedings</th>
                      <th className="px-4 py-2 text-left">Bench</th>
                      <th className="px-4 py-2 text-left">Order</th>
                      <th className="px-4 py-2 text-left">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewAllModalData.data.map((h, i) => (
                      <tr key={i} className="border-t border-white/10">
                        <td className="px-4 py-2 whitespace-nowrap">{h["Date of Hearing"] || "—"}</td>
                        <td className="px-4 py-2">{h.Status || "—"}</td>
                        <td className="px-4 py-2 text-sm max-w-md">{h["Proceedings of the day"] || "—"}</td>
                        <td className="px-4 py-2">{h.Bench || "—"}</td>
                        <td className="px-4 py-2">
                          {h.Order_action?.url ? (
                            <a href={h.Order_action.url} target="_blank" rel="noopener noreferrer" className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors">
                              View
                            </a>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-2">
                          {h.Order_action?.url ? (
                            <a
                              href={`${HARYANA_API_BASE}/api/rera/hr/downloadOrder?sessionId=${getSessionId()}&url=${encodeURIComponent(h.Order_action.url)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-400 hover:text-green-300"
                            >
                              <Download className="w-5 h-5" />
                            </a>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {viewAllModalData.type === "grid" && viewAllModalData.data && (
                <table className="w-full border border-white/20">
                  <thead className="bg-white/10 sticky top-0">
                    <tr>
                      {viewAllModalData.data.headers?.map(h => (
                        <th key={h} className="px-4 py-2 text-left">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {viewAllModalData.data.rows?.map((row, i) => (
                      <tr key={i} className="border-t border-white/10">
                        {viewAllModalData.data.headers?.map(h => (
                          <td key={h} className="px-4 py-2">
                            {row[h]?.type === "hearing" ? (
                              <button
                                onClick={() => handleViewHearing(row[h])}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                View
                              </button>
                            ) : row[h]?.type === "order" ? (
                              <a
                                href={`${API_BASE}/api/rera/up/downloadOrder?sessionId=${getSessionId()}&url=${encodeURIComponent(row[h].downloadLink)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 underline"
                              >
                                Download
                              </a>
                            ) : (
                              row[h] || "—"
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-white/20 flex justify-end">
              <button
                onClick={() => setShowViewAllModal(false)}
                className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ReraCaseDetails;
